import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { memo, useCallback, useLayoutEffect, useRef, useState } from "react";
import { Box, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { useBreadcrumbs, useRouter } from "src/services/Router";
import { IMenu, useMenu } from "src/Sitemap";
import { GD } from "../GD";
import { useWindowSize } from "react-use";
import { useMobile } from "src/hooks/useMobile";
import {
	ArrowLeftRight,
	Banknote,
	BarChart3,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	LayoutDashboard,
	Menu as MenuIcon,
	RefreshCw,
	ShieldCheck,
	Shield,
	Users,
} from "lucide-react";
import { ProfileMenu } from "./ProfileMenu";
import { useTranslation } from "react-i18next";

const SIDEBAR_BG = "hsl(40 10% 95%)";
const SIDEBAR_ACCENT = "hsl(40 10% 91%)";
const TEXT = "hsl(0 1% 37%)";
const PRIMARY = "hsl(17 73% 37%)";
const DIVIDER = "#f2ebe7";

const openProjectSelect = () => {
	location.href = "/";
};

const getMenuIcon = (id: string, fallback?: JSX.Element) => {
	const props = { size: 18, strokeWidth: 1.8 };
	switch (id) {
		case "dashboard":
			return <LayoutDashboard {...props} />;
		case "clients":
			return <Users {...props} />;
		case "providers":
			return <ShieldCheck {...props} />;
		case "payouts":
			return <Banknote {...props} />;
		case "users":
			return <Users {...props} />;
		case "processing":
			return <RefreshCw {...props} />;
		case "reports":
			return <BarChart3 {...props} />;
		case "events":
			return <ArrowLeftRight {...props} />;
		case "administration":
			return <Shield {...props} />;
		default:
			return fallback ?? <LayoutDashboard {...props} />;
	}
};

export const LeftMenu = ({ onItemClick }: { onItemClick?: () => void }) => {
	useLocale();
	const { t } = useTranslation();
	const size = useWindowSize();
	const { isMobile } = useMobile();
	const currentPath = useBreadcrumbs();
	const selectedId = currentPath[0]?.node.id;
	const [collapsed, setCollapsed] = useState(false);
	const [menuHovered, setMenuHovered] = useState(false);
	const navRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const indicatorRef = useRef<HTMLDivElement>(null);
	const indicatorAnimatedRef = useRef(false);
	const menuItems = useMenu();
	const compactByWidth = !isMobile && size.width < 1240;
	const small = isMobile ? false : collapsed || compactByWidth;

	// Position the active indicator imperatively (direct DOM writes) instead of via state.
	// Storing it in useState forced a second full LeftMenu render on every navigation,
	// which cascaded into re-rendering every MenuItem twice.
	const registerItemRef = useCallback((id: string, el: HTMLDivElement | null) => {
		itemRefs.current[id] = el;
	}, []);

	useLayoutEffect(() => {
		const indEl = indicatorRef.current;
		if (!indEl) return;

		const activeEl = selectedId ? itemRefs.current[selectedId] : null;
		const navEl = navRef.current;
		if (!activeEl || !navEl) {
			indEl.style.opacity = "0";
			return;
		}

		const elRect = activeEl.getBoundingClientRect();
		const navRect = navEl.getBoundingClientRect();
		indEl.style.top = `${Math.round(navEl.offsetTop + elRect.top - navRect.top)}px`;
		indEl.style.height = `${Math.round(elRect.height)}px`;
		indEl.style.opacity = "1";

		// Enable the slide transition only after the first placement so the indicator
		// doesn't animate in from the top-left corner on initial mount.
		if (!indicatorAnimatedRef.current) {
			requestAnimationFrame(() => {
				indicatorAnimatedRef.current = true;
				if (indicatorRef.current) indicatorRef.current.style.transition = "top 0.3s ease, height 0.3s ease, opacity 0.2s ease";
			});
		}
	}, [selectedId, small, menuItems?.length]);

	return (
		<Box
			sx={{
				overflow: "hidden",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				position: "relative",
				width: small ? "72px" : "232px",
				backgroundColor: SIDEBAR_BG,
				borderRight: `1px solid ${DIVIDER}`,
				padding: "10px 20px 4px",
				transition: "width 0.3s ease",
			}}>
			<Box
				ref={indicatorRef}
				sx={{
					position: "absolute",
					right: "-2px",
					top: "0px",
					width: "4px",
					height: "0px",
					backgroundColor: PRIMARY,
					borderRadius: "2px 0 0 2px",
					opacity: 0,
					pointerEvents: "none",
					zIndex: 2,
					transition: "opacity 0.2s ease",
				}}
			/>
			<ProjectSelector small={small} />
			<Box sx={{ height: "1px", backgroundColor: DIVIDER, margin: "13px 0 14px" }} />

			<div onClick={() => setCollapsed(prev => !prev)} onMouseEnter={() => setMenuHovered(true)} onMouseLeave={() => setMenuHovered(false)}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: small ? "center" : "flex-start",
						gap: "16px",
						minHeight: "34px",
						padding: small ? "7px 0" : "7px 8px",
						marginBottom: "8px",
						borderRadius: "6px",
						color: TEXT,
						cursor: "pointer",
						transition: "color 0.2s ease, background-color 0.2s ease",
						"&:hover": {
							backgroundColor: SIDEBAR_ACCENT,
						},
					}}>
					<Box sx={{ position: "relative", width: "18px", height: "18px", flexShrink: 0, color: "inherit" }}>
						<MenuIcon
							size={18}
							style={{
								position: "absolute",
								inset: 0,
								opacity: menuHovered ? 0 : 1,
								transform: menuHovered ? "scale(.75) rotate(90deg)" : "scale(1) rotate(0deg)",
								transition: "all 0.2s ease",
							}}
						/>
						{small ? (
							<ChevronRight
								size={18}
								style={{
									position: "absolute",
									inset: 0,
									opacity: menuHovered ? 1 : 0,
									transform: menuHovered ? "scale(1) rotate(0deg)" : "scale(.75) rotate(-90deg)",
									transition: "all 0.2s ease",
								}}
							/>
						) : (
							<ChevronLeft
								size={18}
								style={{
									position: "absolute",
									inset: 0,
									opacity: menuHovered ? 1 : 0,
									transform: menuHovered ? "scale(1) rotate(0deg)" : "scale(.75) rotate(90deg)",
									transition: "all 0.2s ease",
								}}
							/>
						)}
					</Box>
					{!small && <Typo sx={{ fontSize: "14px", fontWeight: 600, color: TEXT }}>{t("nav.menu")}</Typo>}
				</Box>
			</div>

			<Box
				ref={navRef}
				sx={{
					display: "flex",
					flexDirection: "column",
					overflow: "auto",
					flex: 1,
					scrollbarWidth: "none",
					position: "relative",
					gap: "2px",
				}}>
				{menuItems?.map(val => {
					return (
						<MenuItem
							data={val}
							key={val.id}
							small={small}
							selected={selectedId === val.id}
							onItemClick={onItemClick}
							setItemRef={registerItemRef}
						/>
					);
				})}
			</Box>
			<Box sx={{ marginTop: "auto", paddingTop: "4px", borderTop: `1px solid ${DIVIDER}` }}>
				<ProfileMenu collapsed={small} />
			</Box>
		</Box>
	);
};

const MenuItem = memo(({
	data,
	small,
	selected,
	onItemClick,
	setItemRef,
}: {
	data: IMenu;
	small: boolean;
	selected: boolean;
	onItemClick?: () => void;
	setItemRef: (id: string, el: HTMLDivElement | null) => void;
}) => {
	const router = useRouter();
	const [hovered, setHovered] = useState(false);
	const itemElRef = useRef<HTMLDivElement | null>(null);
	const title = data.label;
	if (data.type === "ignore") return null;

	//todo check if profile is merchant and only one merchant

	if (data.type === "divider")
		return (
			<Box
				sx={{
					height: "1px",
					margin: "10px 0 10px 0",
					backgroundColor: DIVIDER,
				}}
			/>
		);

	if (data.type === "label") {
		if (small) return null;
		return <div>{data.label}</div>;
	}

	return (
		<div
			ref={el => {
				setItemRef(data.id, el);
				itemElRef.current = el;
			}}
			style={{ position: "relative" }}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}>
			<Box
				sx={{
					cursor: "pointer",
					background: "transparent",
					color: selected ? PRIMARY : TEXT,
					display: "flex",
					gap: "16px",
					minHeight: "34px",
					padding: small ? "7px 0" : "7px 8px",
					transition: "background-color 0.2s ease, color 0.2s ease",
					alignItems: "center",
					borderRadius: "6px",
					marginRight: "0px",
					justifyContent: small ? "center" : "flex-start",
					"&:hover": {
						backgroundColor: SIDEBAR_ACCENT,
					},
				}}
				onClick={() => {
					router.navigate(data.id);
					onItemClick?.();
				}}>
				<Box sx={{ lineHeight: "0px", flexShrink: 0, color: "inherit", display: "flex" }}>{getMenuIcon(data.id, data.icon)}</Box>
				{!small && (
					<Typo sx={{ fontSize: "14px", userSelect: "none", fontWeight: selected ? 800 : 600, color: "inherit" }}>
						{title}
					</Typo>
				)}
			</Box>
			{small &&
				hovered &&
				itemElRef.current &&
				(() => {
					const rect = itemElRef.current!.getBoundingClientRect();
					return (
						<Box
							sx={{
								position: "fixed",
								left: rect.right + "px",
								top: rect.top + "px",
								zIndex: 1000,
								background: "#fff",
								boxShadow: "0 12px 28px rgba(61, 48, 40, 0.14), 0 2px 8px rgba(61, 48, 40, 0.08)",
								borderRadius: "8px",
								padding: "9px 12px",
								whiteSpace: "nowrap",
								display: "flex",
								alignItems: "center",
								gap: "10px",
								cursor: "pointer",
								border: `1px solid hsl(30 10% 90%)`,
							}}
							onClick={() => {
								router.navigate(data.id);
							}}>
							<Typo sx={{ fontSize: "13px", userSelect: "none", fontWeight: 700, color: TEXT }}>
								{title}
							</Typo>
						</Box>
					);
				})()}
		</div>
	);
});
MenuItem.displayName = "MenuItem";

export const ProjectSelector = ({ small }: { small: boolean }) => {
	useLocale();
	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();
	const [hovered, setHovered] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const nameRef = useRef<HTMLDivElement>(null);

	const isOverflowing = hovered && nameRef.current ? nameRef.current.scrollWidth > nameRef.current.clientWidth : false;
	const showTooltip = hovered && (small || isOverflowing);

	if (!project) return null;

	return (
		<div ref={ref} style={{ position: "relative" }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
			<VBox
				sx={{
					padding: small ? "0" : "0",
					cursor: "pointer",
					alignItems: small ? "center" : "unset",
				}}
				onClick={_ => {
					openProjectSelect();
				}}>
				{small ? (
					<ProjectLogoIcon size={36} />
				) : (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "10px",
							height: "36px",
							maxWidth: "192px",
						}}>
						<ProjectLogoIcon size={36} />
						<Box sx={{ overflow: "hidden", minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}>
							<Typo ref={nameRef} sx={{ color: TEXT, fontSize: "15px", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{project?.name}
							</Typo>
							<ChevronDown size={16} color={TEXT} />
						</Box>
					</Box>
				)}
			</VBox>
			{showTooltip &&
				ref.current &&
				(() => {
					const rect = ref.current!.getBoundingClientRect();
					return (
						<Box
							sx={{
								position: "fixed",
								left: (small ? rect.right : rect.right + 4) + "px",
								top: rect.top + "px",
								zIndex: 1000,
								background: "#fff",
								boxShadow: "0 12px 28px rgba(61, 48, 40, 0.14), 0 2px 8px rgba(61, 48, 40, 0.08)",
								borderRadius: "8px",
								padding: "10px 14px",
								whiteSpace: "nowrap",
								display: "flex",
								flexDirection: "column",
								gap: "2px",
								pointerEvents: small ? "auto" : "none",
								cursor: small ? "pointer" : "default",
								border: `1px solid hsl(30 10% 90%)`,
							}}
							onClick={
								small
									? () => {
											openProjectSelect();
										}
									: undefined
							}>
							<Typo variant="small" sx={{ opacity: 0.5 }}>{msg("project")}</Typo>
							<Typo sx={{ fontSize: "14px", fontWeight: 700, color: TEXT }}>{project?.name}</Typo>
						</Box>
					);
				})()}
		</div>
	);
};

const ProjectLogoIcon = ({ size }: { size: number }) => {
	useLocale();
	return (
		<Box
			sx={{
				width: `${size}px`,
				height: `${size}px`,
				minWidth: `${size}px`,
				borderRadius: "6px",
				background: "#23407d82",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				overflow: "hidden",
			}}>
			<svg
				width={Math.round(size * 0.64)}
				height={Math.round(size * 0.64)}
				viewBox="127.8 3.8 21.8 21.8"
				aria-hidden="true"
				focusable="false"
				style={{
					display: "block",
				}}>
				<path
					transform="matrix(1.221,0,0,-1.221,6.0930178,4.0720217)"
					fill="#ffffff"
					fillRule="evenodd"
					d="M100.001-.27C100.014-.249 100.02-.235 100.031-.226 100.039-.216 100.052-.203 100.064-.203H105.503C107.381-3.01 109.27-5.834 111.175-8.679 109.264-11.466 107.354-14.288 105.511-17.111H99.989C101.868-14.306 103.731-11.528 105.616-8.715"
				/>
				<path
					transform="matrix(1.221,0,0,-1.221,6.0930178,4.0720217)"
					fill="#ffffff"
					fillRule="evenodd"
					d="M114.257-13.087C115.221-14.466 116.129-15.761 117.035-17.054 117.024-17.076 117.02-17.09 117.01-17.098 117.002-17.111 116.991-17.125 116.977-17.125H111.496C112.453-15.78 113.322-14.437 114.257-13.087Z"
				/>
				<path
					transform="matrix(1.221,0,0,-1.221,6.0930178,4.0720217)"
					fill="#ffffff"
					fillRule="evenodd"
					d="M111.548-.218H117.037C116.104-1.553 115.183-2.87 114.253-4.203 113.341-2.857 112.448-1.541 111.548-.218Z"
				/>
			</svg>
		</Box>
	);
};
